import { BaseEngine, BaseEnginePropertyConfig, IMetadataProvider, LogError, LogStatus, Metadata, RunView, UserInfo } from "@memberjunction/core";
import { ChannelEntityExtended } from "./ChannelEntityExtended.js";
import { MJCommunicationProviderEntity } from "@memberjunction/core-entities";
import { BaseMessageConverter } from "../MessageConverters/BaseMessageConverter.js";
import { MJGlobal, UUIDsEqual } from "@memberjunction/global";
import { BaseCommunicationProvider } from "@memberjunction/communication-types";
import {
    BCSaaSPlanEntity,
    BCSaaSPlanPriceEntity,
    BCSaaSSubscriptionEntity,
    BCSaaSOrganizationPersonRoleEntity,
    BCSaaSRelationshipRoleEntity,
    BCSaaSChannelTypeEntity
} from "@bluecypress/bcsaas-entities";
import {
    mjBizAppsCommonOrganizationEntity,
    mjBizAppsCommonPersonEntity,
    mjBizAppsCommonRelationshipEntity
} from "@mj-biz-apps/common-entities";
import {
    IzzyChannelActionEntity,
    IzzyIzzyActionCategoryEntity,
    IzzyIzzyActionEntity,
    IzzyOrganizationActionEntity,
    IzzyChannelTypeActionEntity,
    IzzyIzzyActionOrganizationEntity,
    IzzyChannelExtensionEntity,
    IzzyChannelTypeExtensionEntity,
} from "../generated/entity_subclasses.js";
import {
    BCSaaSAIConfigurationEntity,
    BCSaaSOrganizationAIConfigurationEntity,
    BCSaaSOrganizationSettingEntity,
    BCSaaSSettingEntity,
    BCSaaSSettingCategoryEntity,
} from "@bluecypress/bcsaas-entities";

/**
 * Result from setting resolution including metadata about where the value came from
 */
export interface SettingResolutionResult {
    /** The resolved value (or null if not found) */
    value: string | null;
    /** Where the value came from */
    source: 'channel' | 'organization' | 'parent-organization' | 'default' | 'not-found';
    /** The organization ID where the value was found (if applicable) */
    sourceOrganizationId?: string;
    /** The channel ID where the value was found (if applicable) */
    sourceChannelId?: string;
}

/**
 * Default Level values assigned to BC: Relationship Role rows when a new membership is created.
 *
 * Post-BAC the Level concept lives on the per-membership junction (BCSaaSRelationshipRoleEntity.Level)
 * rather than on the role definition itself. Izzy's convention: the named role determines the level
 * at creation time. This map is the single source of truth for that convention; pass the returned
 * level to BCMembershipService.CreateMembership(...).
 *
 * Higher number = more permissive. HasMinimumRoleLevel() checks against these values.
 */
export const ROLE_LEVEL_MAP: Readonly<Record<string, number>> = Object.freeze({
    Owner: 40,
    Admin: 30,
    Member: 20,
    Viewer: 10
});

/**
 * Minimal shape of the membership entries the BCSaaS middleware stamps onto
 * `UserInfo.TenantContext.allMemberships` (BCMembershipInfo in
 * @bluecypress/bcsaas-core, which this package doesn't depend on).
 * Level uses the BCSaaS scale (BC_ROLE_LEVELS: Owner=100, Admin=80, ...),
 * NOT this file's legacy ROLE_LEVEL_MAP.
 */
export interface IzzyTenantMembership {
    organizationID: string;
    organizationRoleID: string;
    roleID: string;
    level: number;
}

export class IzzyMetadataEngine extends BaseEngine<IzzyMetadataEngine> {

    /**
     * Returns the convention-driven Level for a role name (see ROLE_LEVEL_MAP).
     * Returns 0 for unknown role names — callers should validate before passing to CreateMembership
     * if they care about a non-zero level.
     */
    public static GetRoleLevelByName(roleName: string): number {
        return ROLE_LEVEL_MAP[roleName] ?? 0;
    }


    // Account Management
    private _organizations: mjBizAppsCommonOrganizationEntity[] = [];

    // Plans (v2.0; post-BAC the Plan↔Organization link is via Subscription → PlanPrice → Plan)
    private _plans: BCSaaSPlanEntity[] = [];
    private _planPrices: BCSaaSPlanPriceEntity[] = [];
    private _subscriptions: BCSaaSSubscriptionEntity[] = [];

    // Settings
    private _settings: BCSaaSSettingEntity[] = [];
    private _settingCategories: BCSaaSSettingCategoryEntity[] = [];
    private _organizationSettings: BCSaaSOrganizationSettingEntity[] = [];

    // Channels
    private _channels: ChannelEntityExtended[] = [];
    private _channelTypes: BCSaaSChannelTypeEntity[] = [];
    private _communicationProviderEntities: MJCommunicationProviderEntity[] = [];

    // Channel/ChannelType Extensions (post-BAC: Izzy-specific columns split off into 1:1 extension tables)
    // ChannelExtension has: ChannelID, ActionInheritMode, AIConfigurationID, CredentialID
    // ChannelTypeExtension has: ChannelTypeID, ActionInheritMode
    private _channelExtensions: IzzyChannelExtensionEntity[] = [];
    private _channelTypeExtensions: IzzyChannelTypeExtensionEntity[] = [];

    // Channel Actions (v2.0 - replaces Tools/ChannelTools)
    private _channelActions: IzzyChannelActionEntity[] = [];

    // Action Governance (v2.0)
    private _izzyActionCategories: IzzyIzzyActionCategoryEntity[] = [];
    private _izzyActions: IzzyIzzyActionEntity[] = [];
    private _organizationActions: IzzyOrganizationActionEntity[] = [];
    private _channelTypeActions: IzzyChannelTypeActionEntity[] = [];
    private _izzyActionOrganizations: IzzyIzzyActionOrganizationEntity[] = [];

    // Current User's Contact
    private _currentContact: mjBizAppsCommonPersonEntity | null = null;

    // User Organization Membership (v2.3 - Security; post-BAC Status lives on the parent BAC Relationship)
    private _organizationContacts: BCSaaSOrganizationPersonRoleEntity[] = [];
    private _contactRoles: BCSaaSRelationshipRoleEntity[] = [];
    private _relationships: mjBizAppsCommonRelationshipEntity[] = [];

    // AI Configurations (v2.2 — now from BCSaaS)
    private _aiConfigurations: BCSaaSAIConfigurationEntity[] = [];
    private _organizationAIConfigurations: BCSaaSOrganizationAIConfigurationEntity[] = [];

    // Cached instances
    private _transformers: { [key: string]: BaseMessageConverter } = {};
    private _baseCommunicationProviders: { [key: string]: BaseCommunicationProvider } = {};

    // ========================================
    // Account Management Getters
    // ========================================
    public get Organizations(): mjBizAppsCommonOrganizationEntity[] {
        return this._organizations;
    }

    // ========================================
    // Plan Getters (v2.0)
    // ========================================
    public get Plans(): BCSaaSPlanEntity[] {
        return this._plans;
    }

    // ========================================
    // Settings Getters
    // ========================================
    public get Settings(): BCSaaSSettingEntity[] {
        return this._settings;
    }
    public get SettingCategories(): BCSaaSSettingCategoryEntity[] {
        return this._settingCategories;
    }
    public get OrganizationSettings(): BCSaaSOrganizationSettingEntity[] {
        return this._organizationSettings;
    }

    // ========================================
    // Channel Getters
    // ========================================
    public get Channels(): ChannelEntityExtended[] {
        return this._channels;
    }

    public get ChannelTypes(): BCSaaSChannelTypeEntity[] {
        return this._channelTypes;
    }

    public get CommunicationProviderEntities(): MJCommunicationProviderEntity[] {
        return this._communicationProviderEntities;
    }

    // ========================================
    // Channel Action Getters (v2.0)
    // ========================================
    public get ChannelActions(): IzzyChannelActionEntity[] {
        return this._channelActions;
    }

    // ========================================
    // Action Governance Getters (v2.0)
    // ========================================
    public get IzzyActionCategories(): IzzyIzzyActionCategoryEntity[] {
        return this._izzyActionCategories;
    }

    public get IzzyActions(): IzzyIzzyActionEntity[] {
        return this._izzyActions;
    }

    public get OrganizationActions(): IzzyOrganizationActionEntity[] {
        return this._organizationActions;
    }

    public get ChannelTypeActions(): IzzyChannelTypeActionEntity[] {
        return this._channelTypeActions;
    }

    public get IzzyActionOrganizations(): IzzyIzzyActionOrganizationEntity[] {
        return this._izzyActionOrganizations;
    }

    // ========================================
    // User Organization Membership Getters (v2.3 - Security)
    // ========================================
    public get OrganizationContacts(): BCSaaSOrganizationPersonRoleEntity[] {
        return this._organizationContacts;
    }

    public get ContactRoles(): BCSaaSRelationshipRoleEntity[] {
        return this._contactRoles;
    }

    // ========================================
    // AI Configuration Getters (v2.2 — BCSaaS)
    // ========================================
    public get AIConfigurations(): BCSaaSAIConfigurationEntity[] {
        return this._aiConfigurations;
    }

    public get OrganizationAIConfigurations(): BCSaaSOrganizationAIConfigurationEntity[] {
        return this._organizationAIConfigurations;
    }

    /**
     * Gets a BCSaaS AI Configuration by ID
     * @param configId The configuration ID
     * @returns The BCSaaSAIConfigurationEntity or null if not found
     */
    public GetAIConfiguration(configId: string): BCSaaSAIConfigurationEntity | null {
        const config = this._aiConfigurations.find(c => c.ID === configId);
        return config || null;
    }

    /**
     * Gets all active AI Configurations, sorted by Sequence
     * @returns Array of active configurations
     */
    public GetActiveAIConfigurations(): BCSaaSAIConfigurationEntity[] {
        const now = new Date();
        return this._aiConfigurations
            .filter(c => c.Status === 'Active' && (!c.AvailableUntil || c.AvailableUntil > now))
            .sort((a, b) => a.Sequence - b.Sequence);
    }

    /**
     * Gets the default AI Configuration for an organization via the OrganizationAIConfiguration junction table.
     * Replaces the old Organization.IzzyAIConfigurationID direct FK pattern.
     * @param organizationId The organization ID
     * @returns The default BCSaaSAIConfigurationEntity for the org, or null if none set
     */
    public GetOrganizationDefaultAIConfiguration(organizationId: string): BCSaaSAIConfigurationEntity | null {
        const orgAIConfig = this._organizationAIConfigurations.find(
            oac => oac.OrganizationID === organizationId && oac.IsDefault && oac.IsActive
        );
        if (!orgAIConfig) {
            return null;
        }
        return this.GetAIConfiguration(orgAIConfig.AIConfigurationID);
    }

    /**
     * Gets the AI Configuration ID for an organization (convenience for getting just the ID).
     * Walks up the org hierarchy if the direct org has no config set.
     * @param organizationId The organization ID
     * @returns The AI Configuration ID or null
     */
    public GetOrganizationAIConfigurationID(organizationId: string): string | null {
        // Check direct org
        const config = this.GetOrganizationDefaultAIConfiguration(organizationId);
        if (config) {
            return config.ID;
        }

        // Walk up parent organizations
        const ancestors = this.GetOrganizationAncestors(organizationId);
        for (const ancestor of ancestors) {
            const ancestorConfig = this.GetOrganizationDefaultAIConfiguration(ancestor.ID);
            if (ancestorConfig) {
                return ancestorConfig.ID;
            }
        }

        return null;
    }

    // ========================================
    // Current Contact Getters
    // ========================================

    /**
     * Gets the Contact entity for the currently logged-in user.
     * This is resolved by matching the user's email to a Contact record.
     * If no matching Contact exists, one is auto-created.
     */
    public get CurrentContact(): mjBizAppsCommonPersonEntity | null {
        return this._currentContact;
    }

    /**
     * Convenience getter for the current user's ContactID.
     * Returns null if no contact is loaded.
     */
    public get CurrentContactID(): string | null {
        return this._currentContact?.ID ?? null;
    }

    // ========================================
    // Singleton
    // ========================================
    public static get Instance(): IzzyMetadataEngine {
        return super.getInstance<IzzyMetadataEngine>();
    }

    // ========================================
    // Configuration
    // ========================================
    public async Config(forceRefresh?: boolean, contextUser?: UserInfo, provider?: IMetadataProvider) {
        const params: Partial<BaseEnginePropertyConfig>[] = [
            // Account Management
            {
                PropertyName: '_organizations',
                EntityName: 'MJ_BizApps_Common: Organizations',
                Type: 'entity',
                CacheLocal: true
            },
            // Plans (v2.0)
            {
                PropertyName: '_plans',
                EntityName: 'BC: Plans',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_planPrices',
                EntityName: 'BC: Plan Prices',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_subscriptions',
                EntityName: 'BC: Subscriptions',
                Type: 'entity',
                CacheLocal: true
            },
            // Settings
            {
                PropertyName: '_settings',
                EntityName: 'BC: Settings',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_settingCategories',
                EntityName: 'BC: Setting Categories',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_organizationSettings',
                EntityName: 'BC: Organization Settings',
                Type: 'entity',
                CacheLocal: true
            },
            // Channels
            {
                PropertyName: '_channels',
                EntityName: 'BC: Channels',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_channelTypes',
                EntityName: 'BC: Channel Types',
                Type: 'entity',
                CacheLocal: true
            },
            // Channel + ChannelType extensions (1:1 with the BCSaaS canonical entities)
            {
                PropertyName: '_channelExtensions',
                EntityName: 'Channel Extensions',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_channelTypeExtensions',
                EntityName: 'Channel Type Extensions',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_communicationProviderEntities',
                EntityName: 'MJ: Communication Providers',
                Type: 'entity',
                CacheLocal: true
            },
            // Channel Actions (v2.0)
            {
                PropertyName: '_channelActions',
                EntityName: 'Channel Actions',
                Type: 'entity',
                CacheLocal: true
            },
            // Action Governance (v2.0)
            {
                PropertyName: '_izzyActionCategories',
                EntityName: 'Izzy Action Categories',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_izzyActions',
                EntityName: 'Izzy Actions',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_organizationActions',
                EntityName: 'Organization Actions',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_channelTypeActions',
                EntityName: 'Channel Type Actions',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_izzyActionOrganizations',
                EntityName: 'Izzy Action Organizations',
                Type: 'entity',
                CacheLocal: true
            },
            // User Organization Membership (v2.3 - Security; Status lives on the parent BAC Relationship)
            {
                PropertyName: '_organizationContacts',
                EntityName: 'BC: Organization Person Roles',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_contactRoles',
                EntityName: 'BC: Relationship Roles',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_relationships',
                EntityName: 'MJ_BizApps_Common: Relationships',
                Type: 'entity',
                CacheLocal: true
            },
            // AI Configurations (v2.2 — BCSaaS)
            {
                PropertyName: '_aiConfigurations',
                EntityName: 'BC: AI Configurations',
                Type: 'entity',
                CacheLocal: true
            },
            {
                PropertyName: '_organizationAIConfigurations',
                EntityName: 'BC: Organization AI Configurations',
                Type: 'entity',
                CacheLocal: true
            }
        ];

        const refresh: boolean = forceRefresh || false;
        const loadResult = await this.Load(params, provider!, refresh, contextUser);
        return loadResult;
    }

    protected override async AdditionalLoading(contextUser?: UserInfo): Promise<void> {
        // Wire up ChannelActions to Channels
        for (const channel of this._channels) {
            channel.ChannelActions = this._channelActions.filter(
                ca => ca.ChannelID === channel.ID && ca.Status === 'Active'
            );
        }

        // Load or create the current user's Contact record
        await this.loadCurrentContact(contextUser);
    }

    /**
     * Loads the Contact record for the current user by matching email.
     * If no Contact exists, creates one automatically.
     */
    private async loadCurrentContact(currentUser: UserInfo): Promise<void> {
        const user = currentUser || Metadata.Provider.CurrentUser;

        if (!user?.Email) {
            LogError('IzzyMetadataEngine: Cannot load current contact - no user email available');
            return;
        }

        try {
            // Try to find existing Person by email
            const rv = new RunView();
            const result = await rv.RunView<mjBizAppsCommonPersonEntity>({
                EntityName: 'MJ_BizApps_Common: People',
                ExtraFilter: `Email = '${user.Email.replace(/'/g, "''")}'`
            }, user);

            if (result.Success && result.Results.length > 0) {
                // Found existing contact
                this._currentContact = result.Results[0];
                LogStatus(`IzzyMetadataEngine: Loaded Contact for user ${user.Email}`);
            } else {
                // No contact found - create one
                this._currentContact = await this.createContactForUser(user);
                if (this._currentContact) {
                    LogStatus(`IzzyMetadataEngine: Created new Contact for user ${user.Email}`);
                }
            }
        } catch (error) {
            LogError(`IzzyMetadataEngine: Error loading/creating contact: ${error}`);
        }
    }

    /**
     * Creates a new Contact record for a user.
     * Parses the user's name into first/last name components.
     */
    private async createContactForUser(user: UserInfo): Promise<mjBizAppsCommonPersonEntity | null> {
        try {
            const md = new Metadata();
            const contact = await md.GetEntityObject<mjBizAppsCommonPersonEntity>('MJ_BizApps_Common: People', user);

            // Parse name - try to split into first/last
            const nameParts = (user.Name || '').trim().split(/\s+/);
            const firstName = nameParts[0] || user.Email.split('@')[0];
            const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

            contact.Email = user.Email;
            contact.FirstName = firstName;
            contact.LastName = lastName;
            contact.Status = 'Active';

            const saveResult = await contact.Save();
            if (!saveResult) {
                LogError(`IzzyMetadataEngine: Failed to save new Contact for ${user.Email}`);
                return null;
            }

            return contact;
        } catch (error) {
            LogError(`IzzyMetadataEngine: Error creating contact: ${error}`);
            return null;
        }
    }

    // ========================================
    // Channel Helpers
    // ========================================
    public GetChannel(channelID: string): ChannelEntityExtended | null {
        const channel: ChannelEntityExtended | undefined = this._channels.find(channel => channel.ID === channelID);
        return channel || null;
    }

    public GetChannelType(channelTypeID: string): BCSaaSChannelTypeEntity | null {
        const channelType: BCSaaSChannelTypeEntity | undefined = this._channelTypes.find(ct => ct.ID === channelTypeID);
        return channelType || null;
    }

    /**
     * Gets the Izzy.ChannelExtension row for a given Channel.
     *
     * Post-BAC: Channel-specific fields (ActionInheritMode, AIConfigurationID, CredentialID) moved off
     * BCSaaS.Channel into Izzy.ChannelExtension (1:1). This helper does the sync lookup callers used
     * to do as `channel.CredentialID` directly.
     *
     * @returns The extension row, or null if none exists for this channel
     */
    public GetChannelExtension(channelID: string): IzzyChannelExtensionEntity | null {
        const ext: IzzyChannelExtensionEntity | undefined = this._channelExtensions.find(e => e.ChannelID === channelID);
        return ext || null;
    }

    /**
     * Gets the Izzy.ChannelTypeExtension row for a given ChannelType.
     *
     * Post-BAC: ActionInheritMode moved off BCSaaS.ChannelType into Izzy.ChannelTypeExtension (1:1).
     */
    public GetChannelTypeExtension(channelTypeID: string): IzzyChannelTypeExtensionEntity | null {
        const ext: IzzyChannelTypeExtensionEntity | undefined = this._channelTypeExtensions.find(e => e.ChannelTypeID === channelTypeID);
        return ext || null;
    }

    // ========================================
    // Communication Provider Helpers
    // ========================================
    public GetCommunicationProviderEntity(providerID: string): MJCommunicationProviderEntity | null {
        const provider: MJCommunicationProviderEntity | undefined = this._communicationProviderEntities.find(provider => provider.ID === providerID);
        return provider || null;
    }

    public GetCommunicationProviderEntityByName(providerName: string): MJCommunicationProviderEntity | null {
        const provider: MJCommunicationProviderEntity | undefined = this._communicationProviderEntities.find(provider => provider.Name === providerName);
        return provider || null;
    }

    /**
      * Gets an instance of the class for the specified provider. The provider must be one of the providers that are configured in the system.
      * The first time a provider is fetched, it is cached internally for future reuse.
      * @param providerName
      * @returns
      */
    public GetBaseCommunicationProviderByName(providerName: string): BaseCommunicationProvider | null {
        if (this._baseCommunicationProviders[providerName]) {
            return this._baseCommunicationProviders[providerName];
        }

        const instance: BaseCommunicationProvider | null = MJGlobal.Instance.ClassFactory.CreateInstance<BaseCommunicationProvider>(BaseCommunicationProvider, providerName);
        if (!instance) {
            LogError(`Provider ${providerName} not found.`);
            return null;
        }

        // make sure the class we got back is NOT an instance of the base class, that is the default behavior of CreateInstance if we
        // dont have a registration for the class we are looking for
        if (instance.constructor.name === 'BaseCommunicationProvider') {
            LogError(`Provider ${providerName} returned an instance of the base class.`);
            return null;
        }

        // we got a valid instance of the sub-class we were looking for
        this._baseCommunicationProviders[providerName] = instance;
        return instance;
    }

    // ========================================
    // Channel Action Helpers (v2.0)
    // ========================================

    /**
     * Gets all ChannelAction records for a specific channel
     * @param channelId The channel ID
     * @returns Array of IzzyChannelActionEntity for the channel
     */
    public GetChannelActions(channelId: string): IzzyChannelActionEntity[] {
        return this._channelActions.filter(ca => ca.ChannelID === channelId);
    }

    /**
     * Gets only active ChannelAction records for a specific channel
     * @param channelId The channel ID
     * @returns Array of active IzzyChannelActionEntity for the channel, sorted by Sequence
     */
    public GetActiveChannelActions(channelId: string): IzzyChannelActionEntity[] {
        return this._channelActions
            .filter(ca => ca.ChannelID === channelId && ca.Status === 'Active')
            .sort((a, b) => a.Sequence - b.Sequence);
    }

    /**
     * Gets a specific ChannelAction by ID
     * @param channelActionId The ChannelAction ID
     * @returns The IzzyChannelActionEntity or null if not found
     */
    public GetChannelAction(channelActionId: string): IzzyChannelActionEntity | null {
        const action = this._channelActions.find(ca => ca.ID === channelActionId);
        return action || null;
    }

    // ========================================
    // Organization Helpers
    // ========================================

    /**
     * Gets an Organization by ID
     * @param organizationId The Organization ID
     * @returns The mjBizAppsCommonOrganizationEntity or null if not found
     */
    public GetOrganization(organizationId: string): mjBizAppsCommonOrganizationEntity | null {
        const org = this._organizations.find(o => o.ID === organizationId);
        return org || null;
    }

    /**
     * Gets all ancestor organizations for a given organization (parent chain)
     * Returns organizations from immediate parent to root, in order
     * @param organizationId The Organization ID to get ancestors for
     * @returns Array of mjBizAppsCommonOrganizationEntity representing the parent chain
     */
    public GetOrganizationAncestors(organizationId: string): mjBizAppsCommonOrganizationEntity[] {
        const ancestors: mjBizAppsCommonOrganizationEntity[] = [];
        let currentOrg = this.GetOrganization(organizationId);

        while (currentOrg && currentOrg.ParentID) {
            const parent = this.GetOrganization(currentOrg.ParentID);
            if (parent) {
                ancestors.push(parent);
                currentOrg = parent;
            } else {
                break;
            }
        }

        return ancestors;
    }

    // ========================================
    // User Permission Helpers (v2.3 - Security)
    // ========================================

    /**
     * Post-BAC: Status no longer lives on the OrganizationPersonRole junction; it lives on the
     * parent BAC Relationship. This helper joins the junction back to its Relationship and
     * returns true iff the membership is currently Active.
     *
     * Public so any UI / service can check membership-active without redoing the join.
     */
    public IsActiveMembership(oc: BCSaaSOrganizationPersonRoleEntity): boolean {
        const rel = this._relationships.find(r => r.ID === oc.RelationshipID);
        return rel?.Status === 'Active';
    }

    /**
     * The current user's full membership list as stamped by the BCSaaS middleware
     * onto UserInfo.TenantContext (batched with the CurrentUser query at login).
     *
     * This is the only CONTRACT-GUARANTEED complete client-side source of "which
     * orgs does the current user belong to". Since BCSaaS v1.5, single RunViews on
     * OrganizationPersonRole are tenant-filtered to the current-context org for
     * non-system users (SkipTenantFilter does not survive the GraphQL transport).
     * The engine's own cached load currently arrives via MJ's dataset path, which
     * bypasses that filter — but that is an implementation detail, not a contract;
     * cross-org decisions must come from this stamped list, never from RunViews.
     *
     * Returns null when no stamped membership list is available (server-side and
     * system-user contexts — there the legacy cache-based paths remain correct).
     */
    public get CurrentUserTenantMemberships(): IzzyTenantMembership[] | null {
        // ContextUser is the UserInfo this engine was configured with (BaseEngine
        // stores it at Load time); client-side Config() without an explicit user
        // leaves it unset, so fall back to the provider's current user.
        const user = this.ContextUser ?? Metadata.Provider?.CurrentUser;
        const ctx = user?.TenantContext as { allMemberships?: IzzyTenantMembership[] } | undefined;
        return ctx?.allMemberships?.length ? ctx.allMemberships : null;
    }

    /**
     * Membership Level (BCSaaS scale, see BC_ROLE_LEVELS) of the current user in
     * the given organization, or null when they hold no active membership there.
     * Prefers the middleware-stamped tenant context; falls back to the OPR cache.
     */
    public GetCurrentUserMembershipLevel(organizationId: string): number | null {
        if (!organizationId) {
            return null;
        }

        const memberships = this.CurrentUserTenantMemberships;
        if (memberships) {
            const m = memberships.find(mm => UUIDsEqual(mm.organizationID, organizationId));
            return m ? m.level : null;
        }

        const contactId = this.CurrentContactID;
        if (!contactId) {
            return null;
        }
        const oc = this._organizationContacts.find(
            c => UUIDsEqual(c.PersonID, contactId) &&
                 UUIDsEqual(c.OrganizationID, organizationId) &&
                 this.IsActiveMembership(c)
        );
        return oc ? oc.Level : null;
    }

    /**
     * True when the current user holds an active membership at Level >= minLevel
     * (BCSaaS scale) in the given organization — or, when organizationId is
     * omitted, in ANY organization. Client-side UX gate only; the trust boundary
     * stays in the DB/RLS.
     */
    public CurrentUserHasMembershipLevel(minLevel: number, organizationId?: string): boolean {
        if (organizationId) {
            const level = this.GetCurrentUserMembershipLevel(organizationId);
            return level !== null && level >= minLevel;
        }

        const memberships = this.CurrentUserTenantMemberships;
        if (memberships) {
            return memberships.some(m => m.level >= minLevel);
        }

        const contactId = this.CurrentContactID;
        if (!contactId) {
            return false;
        }
        return this._organizationContacts.some(
            oc => UUIDsEqual(oc.PersonID, contactId) &&
                  this.IsActiveMembership(oc) &&
                  oc.Level >= minLevel
        );
    }

    /**
     * Gets all organizations the specified user has access to.
     * Returns only Active memberships (Active = parent Relationship.Status === 'Active').
     *
     * For the CURRENT user this prefers the middleware-stamped tenant context
     * (see CurrentUserTenantMemberships) — the guaranteed-complete membership
     * source under BCSaaS tenant filtering. Other contacts (and server/system
     * contexts) use the cache as before.
     *
     * @param contactId The contact ID of the user
     * @returns Array of mjBizAppsCommonOrganizationEntity the user can access
     */
    public GetUserOrganizations(contactId: string): mjBizAppsCommonOrganizationEntity[] {
        if (!contactId) {
            return [];
        }

        const memberships = this.CurrentUserTenantMemberships;
        if (memberships && this._currentContact && UUIDsEqual(contactId, this._currentContact.ID)) {
            return this._organizations.filter(
                o => memberships.some(m => UUIDsEqual(m.organizationID, o.ID))
            );
        }

        const userOrgContacts = this._organizationContacts.filter(
            oc => oc.PersonID === contactId && this.IsActiveMembership(oc)
        );

        const orgIds = userOrgContacts.map(oc => oc.OrganizationID);
        return this._organizations.filter(o => orgIds.includes(o.ID));
    }

    /**
     * Gets all organizations the current logged-in user has access to.
     * Convenience wrapper around GetUserOrganizations using CurrentContactID.
     *
     * @returns Array of mjBizAppsCommonOrganizationEntity the current user can access
     */
    public GetCurrentUserOrganizations(): mjBizAppsCommonOrganizationEntity[] {
        if (!this._currentContact) {
            return [];
        }
        return this.GetUserOrganizations(this._currentContact.ID);
    }

    /**
     * Checks if a user has access to a specific organization.
     *
     * @param contactId The contact ID
     * @param organizationId The organization ID to check
     * @returns true if user has Active access to the organization
     */
    public HasOrganizationAccess(contactId: string, organizationId: string): boolean {
        if (!contactId || !organizationId) {
            return false;
        }

        // Current user: the stamped tenant context is the guaranteed-complete
        // membership list under BCSaaS tenant filtering (see the getter's doc).
        const memberships = this.CurrentUserTenantMemberships;
        if (memberships && this._currentContact && UUIDsEqual(contactId, this._currentContact.ID)) {
            return memberships.some(m => UUIDsEqual(m.organizationID, organizationId));
        }

        return this._organizationContacts.some(
            oc => oc.PersonID === contactId &&
                  oc.OrganizationID === organizationId &&
                  this.IsActiveMembership(oc)
        );
    }

    /**
     * Checks if the current logged-in user has access to a specific organization.
     *
     * @param organizationId The organization ID to check
     * @returns true if current user has Active access to the organization
     */
    public CurrentUserHasOrganizationAccess(organizationId: string): boolean {
        if (!this._currentContact) {
            return false;
        }
        return this.HasOrganizationAccess(this._currentContact.ID, organizationId);
    }

    /**
     * Gets the user's role in a specific organization.
     *
     * @param contactId The contact ID
     * @param organizationId The organization ID
     * @returns BCSaaSRelationshipRoleEntity or null if user has no access
     */
    public GetUserRoleInOrganization(contactId: string, organizationId: string): BCSaaSRelationshipRoleEntity | null {
        if (!contactId || !organizationId) {
            return null;
        }

        const orgContact = this._organizationContacts.find(
            oc => oc.PersonID === contactId &&
                  oc.OrganizationID === organizationId &&
                  this.IsActiveMembership(oc)
        );

        if (!orgContact || !orgContact.RoleID) {
            return null;
        }

        return this._contactRoles.find(r => r.ID === orgContact.RoleID) || null;
    }

    /**
     * Gets the current user's role in a specific organization.
     *
     * @param organizationId The organization ID
     * @returns BCSaaSRelationshipRoleEntity or null if user has no access
     */
    public GetCurrentUserRoleInOrganization(organizationId: string): BCSaaSRelationshipRoleEntity | null {
        if (!this._currentContact) {
            return null;
        }
        return this.GetUserRoleInOrganization(this._currentContact.ID, organizationId);
    }

    /**
     * Checks if a user has a specific role level or higher in an organization.
     * Role levels typically range from 10 (Viewer) to 40 (Owner).
     *
     * @param contactId The contact ID
     * @param organizationId The organization ID
     * @param minLevel The minimum role level required (e.g., 30 for Admin)
     * @returns true if user has required role level or higher
     */
    public HasMinimumRoleLevel(contactId: string, organizationId: string, minLevel: number): boolean {
        const role = this.GetUserRoleInOrganization(contactId, organizationId);
        return role ? role.Level >= minLevel : false;
    }

    /**
     * Gets all organization IDs the user has access to.
     * Useful for building SQL IN clauses for filtering queries.
     *
     * @param contactId The contact ID
     * @returns Array of organization IDs (UUIDs)
     */
    public GetUserOrganizationIds(contactId: string): string[] {
        if (!contactId) {
            return [];
        }

        return this._organizationContacts
            .filter(oc => oc.PersonID === contactId && this.IsActiveMembership(oc))
            .map(oc => oc.OrganizationID);
    }

    // ========================================
    // Setting Resolution Helpers
    // ========================================

    /**
     * Gets the effective value of a setting for a given channel, following the inheritance hierarchy.
     *
     * Resolution Order (highest to lowest priority):
     * 1. Channel-scoped setting (Scope='Channel', ChannelID=channelId)
     * 2. Organization-scoped setting (Scope='Organization', for channel's org)
     * 3. Parent Organization settings (walk up the org hierarchy)
     * 4. System Default (DefaultValue from Setting definition)
     *
     * @param settingKey The Setting.Key to look up
     * @param channelId The Channel ID to resolve settings for
     * @returns SettingResolutionResult with value and source information
     */
    public GetSettingValue(settingKey: string, channelId: string): SettingResolutionResult {
        // Find the channel
        const channel = this.GetChannel(channelId);
        if (!channel) {
            return { value: null, source: 'not-found' };
        }

        // Find the setting definition
        const settingDef = this._settings.find(s => s.Key === settingKey);
        if (!settingDef) {
            return { value: null, source: 'not-found' };
        }

        // 1. Check for channel-scoped setting
        const channelSetting = this._organizationSettings.find(os => {
            if (os.OrganizationID !== channel.OrganizationID) return false;
            if (os.SettingID !== settingDef.ID) return false;
            return os.Scope === 'Channel' && os.ScopeEntityID === channelId;
        });

        if (channelSetting) {
            return {
                value: channelSetting.Value,
                source: 'channel',
                sourceOrganizationId: channel.OrganizationID,
                sourceChannelId: channelId
            };
        }

        // 2. Check for organization-scoped setting
        const orgSetting = this._organizationSettings.find(os => {
            if (os.OrganizationID !== channel.OrganizationID) return false;
            if (os.SettingID !== settingDef.ID) return false;
            return os.Scope === 'Organization';
        });

        if (orgSetting) {
            return {
                value: orgSetting.Value,
                source: 'organization',
                sourceOrganizationId: channel.OrganizationID
            };
        }

        // 3. Walk up the organization hierarchy (parent orgs)
        const ancestorOrgs = this.GetOrganizationAncestors(channel.OrganizationID);
        for (const parentOrg of ancestorOrgs) {
            const parentSetting = this._organizationSettings.find(os => {
                if (os.OrganizationID !== parentOrg.ID) return false;
                if (os.SettingID !== settingDef.ID) return false;
                // Only organization-scoped settings in parent orgs
                return os.Scope === 'Organization';
            });

            if (parentSetting) {
                return {
                    value: parentSetting.Value,
                    source: 'parent-organization',
                    sourceOrganizationId: parentOrg.ID
                };
            }
        }

        // 4. Fall back to default value from Setting definition
        if (settingDef.DefaultValue !== null && settingDef.DefaultValue !== undefined) {
            return {
                value: settingDef.DefaultValue,
                source: 'default'
            };
        }

        return { value: null, source: 'not-found' };
    }

    /**
     * Gets the effective value of a setting for an organization (without channel context).
     * Use this when you need org-level settings without channel-specific overrides.
     *
     * Resolution Order:
     * 1. Organization-scoped setting
     * 2. Parent Organization settings (walk up the org hierarchy)
     * 3. System Default (DefaultValue from Setting definition)
     *
     * @param settingKey The Setting.Key to look up
     * @param organizationId The Organization ID to resolve settings for
     * @returns SettingResolutionResult with value and source information
     */
    public GetOrganizationSettingValue(settingKey: string, organizationId: string): SettingResolutionResult {
        // Find the setting definition
        const settingDef = this._settings.find(s => s.Key === settingKey);
        if (!settingDef) {
            return { value: null, source: 'not-found' };
        }

        // 1. Check for organization-scoped setting
        const orgSetting = this._organizationSettings.find(os => {
            if (os.OrganizationID !== organizationId) return false;
            if (os.SettingID !== settingDef.ID) return false;
            return os.Scope === 'Organization';
        });

        if (orgSetting) {
            return {
                value: orgSetting.Value,
                source: 'organization',
                sourceOrganizationId: organizationId
            };
        }

        // 2. Walk up the organization hierarchy
        const ancestorOrgs = this.GetOrganizationAncestors(organizationId);
        for (const parentOrg of ancestorOrgs) {
            const parentSetting = this._organizationSettings.find(os => {
                if (os.OrganizationID !== parentOrg.ID) return false;
                if (os.SettingID !== settingDef.ID) return false;
                return os.Scope === 'Organization';
            });

            if (parentSetting) {
                return {
                    value: parentSetting.Value,
                    source: 'parent-organization',
                    sourceOrganizationId: parentOrg.ID
                };
            }
        }

        // 3. Fall back to default
        if (settingDef.DefaultValue !== null && settingDef.DefaultValue !== undefined) {
            return {
                value: settingDef.DefaultValue,
                source: 'default'
            };
        }

        return { value: null, source: 'not-found' };
    }

    /**
     * Convenience method to get a setting value as a string (just the value, no metadata)
     * @param settingKey The Setting.Key to look up
     * @param channelId The Channel ID to resolve settings for
     * @returns The setting value or null
     */
    public GetSettingValueSimple(settingKey: string, channelId: string): string | null {
        return this.GetSettingValue(settingKey, channelId).value;
    }

    /**
     * Convenience method to get a setting value as a boolean
     * @param settingKey The Setting.Key to look up
     * @param channelId The Channel ID to resolve settings for
     * @param defaultValue Default if setting not found or not parseable
     * @returns The setting value as boolean
     */
    public GetSettingValueBoolean(settingKey: string, channelId: string, defaultValue: boolean = false): boolean {
        const result = this.GetSettingValue(settingKey, channelId);
        if (result.value === null) return defaultValue;
        return result.value.toLowerCase() === 'true';
    }

    /**
     * Convenience method to get a setting value as a number
     * @param settingKey The Setting.Key to look up
     * @param channelId The Channel ID to resolve settings for
     * @param defaultValue Default if setting not found or not parseable
     * @returns The setting value as number
     */
    public GetSettingValueNumber(settingKey: string, channelId: string, defaultValue: number = 0): number {
        const result = this.GetSettingValue(settingKey, channelId);
        if (result.value === null) return defaultValue;
        const parsed = parseFloat(result.value);
        return isNaN(parsed) ? defaultValue : parsed;
    }

    // ========================================
    // Plan Helpers (v2.0)
    // ========================================

    /**
     * Gets a Plan by ID
     * @param planId The Plan ID
     * @returns The BCSaaSPlanEntity or null if not found
     */
    public GetPlan(planId: string): BCSaaSPlanEntity | null {
        const plan = this._plans.find(p => p.ID === planId);
        return plan || null;
    }

    /**
     * Gets a Plan by name
     * @param name The Plan name
     * @returns The BCSaaSPlanEntity or null if not found
     */
    public GetPlanByName(name: string): BCSaaSPlanEntity | null {
        const plan = this._plans.find(p => p.Name === name);
        return plan || null;
    }

    /**
     * Gets a Plan by level
     * @param level The Plan level
     * @returns The BCSaaSPlanEntity or null if not found
     */
    public GetPlanByLevel(level: number): BCSaaSPlanEntity | null {
        const plan = this._plans.find(p => p.Level === level);
        return plan || null;
    }

    /**
     * Gets the Plan currently in effect for an organization.
     *
     * Post-BAC the Plan no longer lives on Organization directly. The chain is:
     *   Organization -> BC: Subscription (Active or Trialing) -> BC: PlanPrice -> BC: Plan
     *
     * @param organizationId The organization ID
     * @returns The BCSaaSPlanEntity or null if the org has no current subscription
     */
    public GetOrganizationPlan(organizationId: string): BCSaaSPlanEntity | null {
        if (!organizationId) {
            return null;
        }
        // Trialing is treated as currently entitled to the plan, matching the
        // intent of the old direct Organization.PlanID FK semantics.
        const subscription = this._subscriptions.find(
            s => s.OrganizationID === organizationId &&
                 (s.Status === 'Active' || s.Status === 'Trialing')
        );
        if (!subscription) {
            return null;
        }
        const planPrice = this._planPrices.find(pp => pp.ID === subscription.PlanPriceID);
        if (!planPrice) {
            return null;
        }
        return this.GetPlan(planPrice.PlanID);
    }

    // ========================================
    // Action Governance Helpers (v2.0)
    // ========================================

    /**
     * Gets an IzzyActionCategory by ID
     * @param categoryId The category ID
     * @returns The IzzyIzzyActionCategoryEntity or null if not found
     */
    public GetIzzyActionCategory(categoryId: string): IzzyIzzyActionCategoryEntity | null {
        const category = this._izzyActionCategories.find(c => c.ID === categoryId);
        return category || null;
    }

    /**
     * Gets all root-level IzzyActionCategories (no parent)
     * @returns Array of root IzzyIzzyActionCategoryEntity
     */
    public GetRootIzzyActionCategories(): IzzyIzzyActionCategoryEntity[] {
        return this._izzyActionCategories.filter(c => !c.ParentID);
    }

    /**
     * Gets child categories for a parent category
     * @param parentId The parent category ID
     * @returns Array of child IzzyIzzyActionCategoryEntity
     */
    public GetChildIzzyActionCategories(parentId: string): IzzyIzzyActionCategoryEntity[] {
        return this._izzyActionCategories.filter(c => c.ParentID === parentId);
    }

    /**
     * Gets an IzzyAction by ID
     * @param izzyActionId The IzzyAction ID
     * @returns The IzzyIzzyActionEntity or null if not found
     */
    public GetIzzyAction(izzyActionId: string): IzzyIzzyActionEntity | null {
        const action = this._izzyActions.find(a => a.ID === izzyActionId);
        return action || null;
    }

    /**
     * Gets an IzzyAction by its linked MJ Action ID
     * @param actionId The MJ Action ID
     * @returns The IzzyIzzyActionEntity or null if not found
     */
    public GetIzzyActionByActionID(actionId: string): IzzyIzzyActionEntity | null {
        const action = this._izzyActions.find(a => a.ActionID === actionId);
        return action || null;
    }

    /**
     * Gets all IzzyActions in a category
     * @param categoryId The category ID
     * @returns Array of IzzyIzzyActionEntity in the category
     */
    public GetIzzyActionsByCategory(categoryId: string): IzzyIzzyActionEntity[] {
        return this._izzyActions.filter(a => a.CategoryID === categoryId);
    }

    /**
     * Gets all active IzzyActions
     * @returns Array of active IzzyIzzyActionEntity
     */
    public GetActiveIzzyActions(): IzzyIzzyActionEntity[] {
        return this._izzyActions.filter(a => a.Status === 'Active');
    }

    /**
     * Gets OrganizationActions for an organization
     * @param organizationId The organization ID
     * @returns Array of IzzyOrganizationActionEntity
     */
    public GetOrganizationActions(organizationId: string): IzzyOrganizationActionEntity[] {
        return this._organizationActions.filter(oa => oa.OrganizationID === organizationId);
    }

    /**
     * Gets active OrganizationActions for an organization, sorted by sequence
     * @param organizationId The organization ID
     * @returns Array of active IzzyOrganizationActionEntity sorted by Sequence
     */
    public GetActiveOrganizationActions(organizationId: string): IzzyOrganizationActionEntity[] {
        return this._organizationActions
            .filter(oa => oa.OrganizationID === organizationId && oa.Status === 'Active')
            .sort((a, b) => a.Sequence - b.Sequence);
    }

    /**
     * Gets ChannelTypeActions for an organization and channel type
     * @param organizationId The organization ID
     * @param channelTypeId The channel type ID
     * @returns Array of IzzyChannelTypeActionEntity
     */
    public GetChannelTypeActions(organizationId: string, channelTypeId: string): IzzyChannelTypeActionEntity[] {
        return this._channelTypeActions.filter(
            cta => cta.OrganizationID === organizationId && cta.ChannelTypeID === channelTypeId
        );
    }

    /**
     * Gets active ChannelTypeActions for an organization and channel type, sorted by sequence
     * @param organizationId The organization ID
     * @param channelTypeId The channel type ID
     * @returns Array of active IzzyChannelTypeActionEntity sorted by Sequence
     */
    public GetActiveChannelTypeActions(organizationId: string, channelTypeId: string): IzzyChannelTypeActionEntity[] {
        return this._channelTypeActions
            .filter(cta =>
                cta.OrganizationID === organizationId &&
                cta.ChannelTypeID === channelTypeId &&
                cta.Status === 'Active'
            )
            .sort((a, b) => a.Sequence - b.Sequence);
    }

    /**
     * Gets private action grants for an organization
     * @param organizationId The organization ID
     * @returns Array of IzzyIzzyActionOrganizationEntity
     */
    public GetIzzyActionOrganizations(organizationId: string): IzzyIzzyActionOrganizationEntity[] {
        return this._izzyActionOrganizations.filter(iao => iao.OrganizationID === organizationId);
    }

    /**
     * Checks if an organization has a private action grant
     * @param organizationId The organization ID
     * @param izzyActionId The IzzyAction ID
     * @returns True if the organization has the grant
     */
    public HasPrivateActionGrant(organizationId: string, izzyActionId: string): boolean {
        return this._izzyActionOrganizations.some(
            iao => iao.OrganizationID === organizationId && iao.IzzyActionID === izzyActionId
        );
    }

    // ========================================
    // Transformer Helpers
    // ========================================
    public GetTransformer(driverName: string): BaseMessageConverter | null {
        const instanceName: string = `${driverName}Converter`;

        const cachedTransformer: BaseMessageConverter | undefined = this._transformers[instanceName];
        if (cachedTransformer) {
            return cachedTransformer;
        }

        const instance: BaseMessageConverter | null = MJGlobal.Instance.ClassFactory.CreateInstance<BaseMessageConverter>(BaseMessageConverter, instanceName);
        if (!instance) {
            LogError(`Error: could not create BaseModel instance with driver name: ${driverName}`);
            return null;
        }

        this._transformers[instanceName] = instance;
        return instance;
    }

    // ========================================
    // Metadata Check
    // ========================================
    protected static checkMetadataLoaded(): void {
        if (!IzzyMetadataEngine.Instance.Loaded) {
            throw new Error("Izzy Metadata not loaded, call IzzyMetadataEngine.Config() first.");
        }
    }
}

// tree shaker prevention
export function LoadIzzyMetadataEngine() {
}
